import React, {useState, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  ProgressBar,
  Modal,
  ListGroup,
  Badge,
} from "react-bootstrap";
import {
  FaPaperPlane,
  FaArrowLeft,
  FaTimes,
  FaPlus,
  FaUsers,
  FaSearch,
  FaCheckCircle,
  FaCalendarAlt,
  FaClock,
  FaComments,
} from "react-icons/fa";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import "./CampaignCreator.css"; // Importamos los estilos específicos
import CommunicationSequencer from "../components/CommunicationSequencer";
import CommunicationEditor from "../components/CommunicationEditor";

// --- Componente para el Modal de Envío ---
// Lo movemos fuera del componente principal para evitar que se re-renderice
// y cause parpadeos cada vez que se actualiza el progreso.
const SendingModal = ({
  show,
  progress,
  isComplete,
  totalRecipients,
  onDone,
  sendType,
}) => {
  const sentCount = Math.floor((progress / 100) * totalRecipients);

  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body className="text-center p-4">
        {!isComplete ? (
          <>
            <h4 className="fw-bold">
              {sendType === "schedule" ? "Programando" : "Enviando"}{" "}
              Comunicación...
            </h4>
            <p className="text-muted">Por favor, no cierres esta ventana.</p>
            <ProgressBar
              animated
              now={progress}
              className="my-3"
              style={{height: "20px"}}
            />
            <p className="fw-bold fs-5">
              {sentCount} de {totalRecipients}
            </p>
          </>
        ) : (
          <>
            <FaCheckCircle
              className="text-success mb-3"
              style={{fontSize: "5rem"}}
            />
            <h4 className="fw-bold">
              ¡Comunicación {sendType === "schedule" ? "Programada" : "Enviada"}{" "}
              con Éxito!
            </h4>
            <p className="text-muted">
              {sendType === "schedule"
                ? "Tu comunicación ha sido agendada y se enviará en la fecha y hora especificadas."
                : `Los ${totalRecipients} mensajes han sido puestos en cola para su entrega.`}
            </p>
            <Button variant="primary" onClick={onDone} className="mt-3 w-100">
              Volver al Comunicaciones
            </Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const CommunicationCreator = ({
  communications,
  setCommunications,
  incentives,
  lists,
  contacts,
}) => {
  const {communicationId} = useParams(); // Obtenemos el ID de la comunicación de la URL si existe
  const isEditing = Boolean(communicationId); // Verificamos si estamos en modo edición
  const location = useLocation(); // Hook para obtener datos pasados en la navegación

  const [communicationName, setCommunicationName] = useState("");
  const allLists = lists; // Usamos las listas que vienen por props
  const allIncentives = incentives; // Usamos los incentivos que vienen por props

  // NUEVA ESTRUCTURA: Array de comunicados en lugar de mensaje único
  const [comunicados, setComunicados] = useState([
    {
      id: 1,
      title: "",
      message: "",
      imagePreview: null,
      videoPreview: null,
      poll: null,
      link: null,
      documentPreview: null,
      incentiveId: null,
      delayDays: 0, // Días de retraso desde el comunicado anterior
      delayHours: 0, // Horas adicionales de retraso
    },
  ]);
  const [currentComunicadoIndex, setCurrentComunicadoIndex] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sendType, setSendType] = useState("now"); // 'now' or 'schedule'
  const [scheduledAt, setScheduledAt] = useState("");
  const [step, setStep] = useState(1); // Estado para controlar el paso del creador
  const [selectedLists, setSelectedLists] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false); // Estado para el modal de encuesta
  const [showLinkModal, setShowLinkModal] = useState(false); // Estado para el modal de link
  const [listSearchTerm, setListSearchTerm] = useState(""); // Estado para el buscador de listas
  const [isSending, setIsSending] = useState(false); // Estado para controlar el modal de envío
  const [sendProgress, setSendProgress] = useState(0); // Estado para la barra de progreso
  const [sendComplete, setSendComplete] = useState(false); // Estado para mostrar el mensaje final
  const navigate = useNavigate();

  // Datos del evento si venimos de la página de detalle de un evento
  const eventContext = location.state?.eventId ? location.state : null;
  const eventListId = eventContext
    ? lists.find((l) => l.name.includes(eventContext.eventName))?.id
    : null;

  // Funciones auxiliares para manejar comunicados
  const getCurrentComunicado = () =>
    comunicados[currentComunicadoIndex] || comunicados[0];

  const updateCurrentComunicado = (updates) => {
    setComunicados((prev) =>
      prev.map((com, index) =>
        index === currentComunicadoIndex ? {...com, ...updates} : com
      )
    );
  };

  // Efecto para cargar los datos de la campaña si estamos en modo edición
  React.useEffect(() => {
    if (isEditing) {
      const communicationToEdit = communications.find(
        (c) => c.id.toString() === communicationId
      );
      if (communicationToEdit) {
        setCommunicationName(communicationToEdit.name);

        // Convertir campaña antigua a nueva estructura si es necesario
        if (communicationToEdit.comunicados) {
          setComunicados(communicationToEdit.comunicados);
        } else {
          // Migrar campaña antigua a nueva estructura
          setComunicados([
            {
              id: 1,
              title: "Comunicado Principal",
              message: communicationToEdit.message || "",
              imagePreview: communicationToEdit.image || null,
              videoPreview: communicationToEdit.video || null,
              poll: communicationToEdit.poll || null,
              link: communicationToEdit.link || null,
              documentPreview: communicationToEdit.documentPreview || null,
              incentiveId: communicationToEdit.incentiveId || null,
              delayDays: 0,
              delayHours: 0,
            },
          ]);
        }

        setStartDate(communicationToEdit.startDate || "");
        setEndDate(communicationToEdit.endDate || "");
        setSendType(communicationToEdit.scheduledDate ? "schedule" : "now");
        setScheduledAt(communicationToEdit.scheduledDate || "");
        setSelectedLists(communicationToEdit.sentToListIds || []);
      }
    } else {
      setStartDate(new Date().toISOString().slice(0, 10));
      // Si estamos en el contexto de un evento, pre-seleccionamos su lista
      if (eventListId) {
        setSelectedLists([eventListId]);
      }
    }
  }, [
    communicationId,
    isEditing,
    communications,
    lists,
    location.state,
    eventListId,
  ]);

  // Calculamos el total de destinatarios para mostrarlo en el resumen
  const totalRecipients = useMemo(() => {
    const recipientIds = new Set();
    selectedLists.forEach((listId) => {
      const list = allLists.find((l) => l.id === listId);
      list?.members.forEach((memberId) => recipientIds.add(memberId));
    });
    return recipientIds.size;
  }, [selectedLists, allLists]);

  // Filtra las listas de difusión según el término de búsqueda
  const filteredLists = useMemo(() => {
    return allLists.filter((list) =>
      list.name.toLowerCase().includes(listSearchTerm.toLowerCase())
    );
  }, [listSearchTerm, allLists]);

  const handleBackClick = () => {
    navigate("/communications");
  };

  const handleNextStep = () => {
    setStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setStep((prev) => prev - 1);
  };
  const handleDiscardAndGoBack = () => {
    setShowConfirmModal(false);
    navigate("/communications");
  };

  const handleSaveAsDraft = () => {
    const newCommunication = {
      id: isEditing ? parseInt(communicationId) : Date.now(),
      name: communicationName,
      comunicados: comunicados, // Nueva estructura de múltiples comunicados
      status:
        sendType === "schedule" && scheduledAt ? "Programada" : "Borrador",
      sentDate: null,
      startDate,
      endDate,
      scheduledDate: sendType === "schedule" ? scheduledAt : null,
      sentToListIds: selectedLists,
      eventId: eventContext?.eventId || null,
      // Mantener compatibilidad con estructura antigua
      message: comunicados[0]?.message || "",
      incentiveId: comunicados[0]?.incentiveId || null,
      image: comunicados[0]?.imagePreview || null,
      video: comunicados[0]?.videoPreview || null,
      poll: comunicados[0]?.poll || null,
      link: comunicados[0]?.link || null,
      documentPreview: comunicados[0]?.documentPreview || null,
    };

    if (isEditing) {
      setCommunications(
        communications.map((c) =>
          c.id.toString() === communicationId ? newCommunication : c
        )
      );
    } else {
      setCommunications((prev) => [...prev, newCommunication]);
    }

    console.log("Guardando borrador:", newCommunication);
    setShowConfirmModal(false);
    navigate("/communications");
  };

  // Estas funciones ya no son necesarias con la nueva estructura
  // Se manejan directamente en el CommunicationEditor

  const handleSavePoll = (pollData) => {
    updateCurrentComunicado({poll: pollData});
    setShowPollModal(false);
  };

  const handleSaveLink = (url) => {
    updateCurrentComunicado({link: url});
    setShowLinkModal(false);
  };

  const handleSendCommunication = () => {
    // 1. Determinar el estado basado en si está programada o no
    const newStatus =
      sendType === "schedule" && scheduledAt ? "Programada" : "Enviada";
    // 1. Crear el objeto de la nueva campaña con nueva estructura
    const newCommunication = {
      id: isEditing ? parseInt(communicationId) : Date.now(),
      name: communicationName,
      comunicados: comunicados, // Nueva estructura de múltiples comunicados
      status: newStatus, // Cambia el estado a "Enviada" o "Programada"
      sentDate: newStatus === "Enviada" ? new Date().toISOString() : null,
      startDate,
      endDate,
      scheduledDate: sendType === "schedule" ? scheduledAt : null,
      sentToListIds: selectedLists,
      eventId: eventContext?.eventId || null,
      // Mantener compatibilidad con estructura antigua
      message: comunicados[0]?.message || "",
      incentiveId: comunicados[0]?.incentiveId || null,
      image: comunicados[0]?.imagePreview || null,
      video: comunicados[0]?.videoPreview || null,
      poll: comunicados[0]?.poll || null,
      link: comunicados[0]?.link || null,
      documentPreview: comunicados[0]?.documentPreview || null,
    };

    // 2. Actualizar el estado global de campañas
    if (isEditing) {
      // Si estamos editando, reemplazamos la campaña existente
      setCommunications(
        communications.map((c) =>
          c.id.toString() === communicationId ? newCommunication : c
        )
      );
    } else {
      // Si es nueva, la añadimos al final
      setCommunications((prev) => [...prev, newCommunication]);
    }
    // Para campañas programadas, no mostramos el modal de envío, simplemente navegamos.
    // El estado "Programada" ya se encarga del resto.
    if (sendType === "schedule" && scheduledAt) {
      navigate("/communications");
      return;
    }
    // 3. Iniciar la simulación de envío (modal de progreso)
    setIsSending(true);
    setSendProgress(0);
    setSendComplete(false);

    const total = totalRecipients;
    if (total === 0) {
      setSendProgress(100);
      setTimeout(() => setSendComplete(true), 500);
      return;
    }

    const interval = setInterval(() => {
      setSendProgress((prev) => {
        const nextProgress = prev + Math.random() * 10; // Simula un progreso variable
        if (nextProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => setSendComplete(true), 500); // Pequeña pausa antes de mostrar el éxito
          return 100;
        }
        return nextProgress;
      });
    }, 200); // Intervalo de actualización
  };

  // --- Componente para el Modal de Creación de Encuestas ---
  const PollCreatorModal = ({show, onHide, onSave}) => {
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState(["", ""]); // Empezamos con 2 opciones

    const handleOptionChange = (index, value) => {
      const newOptions = [...options];
      newOptions[index] = value;
      setOptions(newOptions);
    };

    const handleAddOption = () => {
      if (options.length < 12) {
        // Límite de WhatsApp
        setOptions([...options, ""]);
      }
    };

    const handleRemoveOption = (index) => {
      if (options.length > 2) {
        const newOptions = options.filter((_, i) => i !== index);
        setOptions(newOptions);
      }
    };

    const handleSave = () => {
      const validOptions = options.filter((opt) => opt.trim() !== "");
      if (question.trim() && validOptions.length >= 2) {
        onSave({question, options: validOptions});
        // Reseteamos el estado para la próxima vez
        setQuestion("");
        setOptions(["", ""]);
      }
    };

    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Encuesta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Pregunta</Form.Label>
            <Form.Control
              type="text"
              placeholder="¿Cuál es tu pregunta?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </Form.Group>
          <Form.Label>Opciones</Form.Label>
          {options.map((option, index) => (
            <InputGroup key={index} className="mb-2">
              <Form.Control
                type="text"
                placeholder={`Opción ${index + 1}`}
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
              />
              {options.length > 2 && (
                <Button
                  variant="outline-danger"
                  onClick={() => handleRemoveOption(index)}
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          ))}
          <Button variant="link" size="sm" onClick={handleAddOption}>
            <FaPlus className="me-1" /> Añadir opción
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Crear Encuesta
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // --- Componente para el Modal de Creación de Links ---
  const LinkCreatorModal = ({show, onHide, onSave}) => {
    const [url, setUrl] = useState("");
    const [isValid, setIsValid] = useState(true);

    const handleUrlChange = (e) => {
      const newUrl = e.target.value;
      setUrl(newUrl);
      // Validación simple: debe empezar con http:// o https://
      setIsValid(
        newUrl.trim() === "" ||
          newUrl.startsWith("http://") ||
          newUrl.startsWith("https://")
      );
    };

    const handleSave = () => {
      if (url.trim() && isValid) {
        onSave(url);
        setUrl("");
      } else {
        setIsValid(false);
      }
    };

    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Añadir Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>URL del enlace</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://ejemplo.com"
              value={url}
              onChange={handleUrlChange}
              isInvalid={!isValid}
            />
            <Form.Control.Feedback type="invalid">
              Por favor, ingresa una URL válida (ej: https://...).
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!isValid || !url.trim()}
          >
            Añadir Link
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <Container fluid className="p-4 bg-light">
      <Row className="align-items-center mb-4">
        <Col xs="auto">
          <Button variant="light" onClick={handleBackClick}>
            <FaArrowLeft className="me-2" />
            Volver al Comunicaciones
          </Button>
        </Col>
      </Row>
      <Row>
        {/* Columna Izquierda: Pasos de Creación */}
        <Col md={7} lg={8}>
          <Card className="shadow-sm rounded-4 border-0">
            <Card.Header className="bg-white p-3 border-bottom-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">
                Paso {step}: {step === 1 && "Define la Secuencia"}
                {step === 2 && "Programación y Vigencia"}
                {step === 3 && "Selecciona los Destinatarios"}
                {step === 4 && "Revisa y Envía"}
              </h5>
              <div className="d-flex align-items-center">
                <div className={`step-indicator ${step >= 1 ? "active" : ""}`}>
                  1
                </div>
                <div className="step-connector"></div>
                <div className={`step-indicator ${step >= 2 ? "active" : ""}`}>
                  2
                </div>
                <div className="step-connector"></div>
                <div className={`step-indicator ${step >= 3 ? "active" : ""}`}>
                  3
                </div>
                <div className="step-connector"></div>
                <div className={`step-indicator ${step >= 4 ? "active" : ""}`}>
                  4
                </div>
              </div>
            </Card.Header>
            {/* --- PASO 1: CONTENIDO MULTI-COMUNICADO --- */}
            {step === 1 && (
              <Card.Body className="p-4">
                {/* Nombre de la Comunicación */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    <FaComments className="me-2 text-primary" />
                    Nombre de la Secuencia de Comunicación
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: Secuencia Bienvenida Nuevos Conductores"
                    value={communicationName}
                    onChange={(e) => setCommunicationName(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Dale un nombre descriptivo a tu secuencia de comunicados
                  </Form.Text>
                </Form.Group>

                {/* Secuenciador de Comunicados */}
                <CommunicationSequencer
                  comunicados={comunicados}
                  setComunicados={setComunicados}
                  currentIndex={currentComunicadoIndex}
                  setCurrentIndex={setCurrentComunicadoIndex}
                  incentives={allIncentives}
                  onShowPollModal={(index) => {
                    setCurrentComunicadoIndex(index);
                    setShowPollModal(true);
                  }}
                  onShowLinkModal={(index) => {
                    setCurrentComunicadoIndex(index);
                    setShowLinkModal(true);
                  }}
                />

                {/* Editor del Comunicado Actual */}
                {comunicados.length > 0 && (
                  <CommunicationEditor
                    comunicado={getCurrentComunicado()}
                    index={currentComunicadoIndex}
                    onUpdate={updateCurrentComunicado}
                    incentives={allIncentives}
                    onShowPollModal={(index) => {
                      setCurrentComunicadoIndex(index);
                      setShowPollModal(true);
                    }}
                    onShowLinkModal={(index) => {
                      setCurrentComunicadoIndex(index);
                      setShowLinkModal(true);
                    }}
                    totalComunicados={comunicados.length}
                  />
                )}
              </Card.Body>
            )}

            {/* --- PASO 2: PROGRAMACIÓN Y VIGENCIA --- */}
            {step === 2 && (
              <Card.Body className="p-4">
                <h5 className="mb-3 fw-bold">
                  <FaCalendarAlt className="me-2 text-primary" />
                  Vigencia y Programación del Envío
                </h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Inicio de Vigencia (Opcional)
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Término de Vigencia (Opcional)
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate} // No se puede terminar antes de empezar
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <h6 className="fw-bold">¿Cuándo enviar la comunicación?</h6>
                <Form.Check
                  type="radio"
                  id="send-now"
                  label="Enviar Ahora Mismo"
                  checked={sendType === "now"}
                  onChange={() => setSendType("now")}
                />
                <Form.Check
                  type="radio"
                  id="send-schedule"
                  label="Programar para más tarde"
                  checked={sendType === "schedule"}
                  onChange={() => setSendType("schedule")}
                />
                {sendType === "schedule" && (
                  <Form.Control
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="mt-2"
                  />
                )}
              </Card.Body>
            )}

            {/* --- PASO 3: DESTINATARIOS --- */}
            {step === 3 && (
              <Card.Body className="p-4">
                <h5 className="mb-3 fw-bold">
                  <FaUsers className="me-2 text-primary" /> Seleccionar Listas
                </h5>
                <InputGroup className="mb-3">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Buscar lista por nombre..."
                    value={listSearchTerm}
                    onChange={(e) => setListSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <div className="mb-3 text-muted">
                  <span className="fw-bold text-primary">
                    {totalRecipients}
                  </span>{" "}
                  destinatarios únicos seleccionados.
                </div>
                <ListGroup style={{maxHeight: "400px", overflowY: "auto"}}>
                  {filteredLists.map((list) => (
                    <ListGroup.Item key={list.id} className="py-3">
                      <Form.Check // Deshabilitamos la edición si la lista viene de un evento
                        type="checkbox"
                        id={`list-${list.id}`}
                        checked={selectedLists.includes(list.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLists([...selectedLists, list.id]);
                          } else {
                            setSelectedLists(
                              selectedLists.filter((id) => id !== list.id)
                            );
                          }
                        }}
                        disabled={!!eventContext && list.id === eventListId}
                        label={
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="fw-bold">{list.name}</span>
                              <div className="text-muted small">
                                {list.description}
                              </div>
                            </div>
                            <Badge pill bg="secondary" className="fs-6">
                              {list.members.length} Miembros
                            </Badge>
                          </div>
                        }
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            )}

            {/* --- PASO 4: REVISAR Y ENVIAR --- */}
            {step === 4 && (
              <Card.Body className="p-4">
                <h5 className="mb-4 fw-bold">Resumen de la Secuencia</h5>
                <Row>
                  <Col md={12} className="mb-3">
                    <Card border="light" className="bg-light">
                      <Card.Body>
                        <Card.Title as="h6" className="text-muted mb-2">
                          Nombre de la Secuencia
                        </Card.Title>
                        <h5 className="fw-bold">{communicationName}</h5>
                        <div className="mt-2">
                          <Badge bg="primary" className="me-2">
                            {comunicados.length} Comunicado
                            {comunicados.length > 1 ? "s" : ""}
                          </Badge>
                          <Badge bg="info">
                            {comunicados.filter((c) => c.incentiveId).length}{" "}
                            con Incentivos
                          </Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Card border="light" className="bg-light h-100">
                      <Card.Body>
                        <Card.Title as="h6" className="text-muted mb-2">
                          Destinatarios
                        </Card.Title>
                        <p className="mb-0">
                          <span className="fw-bold">Listas:</span>{" "}
                          {selectedLists.length > 0
                            ? allLists
                                .filter((l) => selectedLists.includes(l.id))
                                .map((l) => l.name)
                                .join(", ")
                            : "Ninguna"}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Card border="light" className="bg-light h-100">
                      <Card.Body>
                        <Card.Title as="h6" className="text-muted mb-2">
                          Programación y Vigencia
                        </Card.Title>
                        <p className="mb-0">
                          <span className="fw-bold">Envío:</span>{" "}
                          {sendType === "now"
                            ? "Inmediato"
                            : `Programado para ${new Date(
                                scheduledAt
                              ).toLocaleString()}`}
                        </p>
                        <p className="mb-0">
                          <span className="fw-bold">Vigencia:</span>{" "}
                          {startDate && endDate
                            ? `Del ${startDate} al ${endDate}`
                            : "No definida"}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Card
                  border="primary"
                  className="text-center mt-3 bg-primary-subtle"
                >
                  <Card.Body className="d-flex justify-content-between align-items-center p-3">
                    <span className="fw-bold fs-5 text-primary">
                      Total de Destinatarios
                    </span>
                    <span className="fw-bold fs-5 text-primary">
                      {totalRecipients}
                    </span>
                  </Card.Body>
                </Card>
              </Card.Body>
            )}

            <Card.Footer className="bg-white p-3 d-flex justify-content-between align-items-center">
              {/* Botón de Guardar Borrador siempre visible a la izquierda */}
              <div>
                <Button variant="outline-secondary" onClick={handleSaveAsDraft}>
                  Guardar
                </Button>
              </div>

              {/* Botones de Navegación a la derecha */}
              <div className="text-end">
                {step > 1 && (
                  <Button
                    variant="secondary"
                    className="me-2"
                    onClick={handlePrevStep}
                  >
                    Atrás
                  </Button>
                )}
                {step < 4 ? (
                  <Button
                    variant="primary"
                    onClick={handleNextStep}
                    disabled={
                      (step === 1 && !communicationName.trim()) ||
                      (step === 2 && sendType === "schedule" && !scheduledAt) ||
                      (step === 3 && selectedLists.length === 0)
                    }
                  >
                    {step === 3 ? "Finalizar y Revisar" : "Siguiente"}
                  </Button>
                ) : (
                  <Button
                    variant={sendType === "schedule" ? "info" : "success"}
                    onClick={handleSendCommunication}
                  >
                    {sendType === "schedule" ? (
                      <FaClock className="me-2" />
                    ) : (
                      <FaPaperPlane className="me-2" />
                    )}
                    {sendType === "schedule"
                      ? "Programar Comunicación"
                      : "Enviar Comunicación Ahora"}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
        </Col>

        {/* Columna Derecha: Información de Ayuda */}
        <Col md={5} lg={4}>
          <Card className="mb-4 shadow-sm rounded-4 border-0">
            <Card.Header className="bg-white p-3 border-bottom-0">
              <h5 className="mb-0 fw-bold">
                <FaComments className="me-2 text-primary" />
                Secuencias de Comunicación
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="mb-3">
                <h6 className="fw-bold text-primary">
                  ¿Qué son las secuencias?
                </h6>
                <p className="small text-muted">
                  Las secuencias te permiten crear múltiples comunicados que se
                  envían automáticamente con intervalos de tiempo específicos.
                </p>
              </div>

              <div className="mb-3">
                <h6 className="fw-bold text-success">Ventajas:</h6>
                <ul className="small text-muted">
                  <li>Comunicación automatizada</li>
                  <li>Mejor engagement con usuarios</li>
                  <li>Seguimiento personalizado</li>
                  <li>Incentivos por comunicado</li>
                </ul>
              </div>

              <div className="mb-3">
                <h6 className="fw-bold text-info">Ejemplos de uso:</h6>
                <ul className="small text-muted">
                  <li>
                    <strong>Bienvenida:</strong> Mensaje inicial + Guía +
                    Beneficios
                  </li>
                  <li>
                    <strong>Promociones:</strong> Anuncio + Recordatorio +
                    Último día
                  </li>
                  <li>
                    <strong>Eventos:</strong> Invitación + Confirmación +
                    Seguimiento
                  </li>
                </ul>
              </div>

              {step === 1 && (
                <div className="alert alert-info small">
                  <strong>Tip:</strong> Usa el secuenciador para ver y organizar
                  todos tus comunicados. Cada uno puede tener contenido único.
                </div>
              )}

              {step === 2 && (
                <div className="alert alert-warning small">
                  <strong>Importante:</strong> La programación afecta a toda la
                  secuencia. Los intervalos entre comunicados se mantienen.
                </div>
              )}

              {step === 3 && (
                <div className="alert alert-success small">
                  <strong>Recuerda:</strong> Todos los comunicados de la
                  secuencia se enviarán a las listas seleccionadas.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Confirmación para Salir */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cambios sin guardar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Parece que tienes cambios sin guardar. ¿Te gustaría guardarlos como un
          borrador antes de salir?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={handleDiscardAndGoBack}>
            Descartar
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveAsDraft}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- Modal para Crear Encuesta --- */}
      <PollCreatorModal
        show={showPollModal}
        onHide={() => setShowPollModal(false)}
        onSave={handleSavePoll}
      />

      {/* --- Modal para Añadir Link --- */}
      <LinkCreatorModal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        onSave={handleSaveLink}
      />

      {/* --- Modal de Envío --- */}
      <SendingModal
        show={isSending}
        progress={sendProgress}
        isComplete={sendComplete}
        totalRecipients={totalRecipients}
        onDone={() => navigate("/communications")}
        sendType={sendType}
      />
    </Container>
  );
};

export default CommunicationCreator;
